import { useMobileViewerUi } from "@/components/common/mobile-viewer-ui-context";
import { FaroIconButton, CloseIcon } from "@faro-lotv/flat-ui";
import { Box, Drawer, MenuList, Stack, Typography } from "@mui/material";
import { useEffect, type JSX, type PropsWithChildren } from "react";

type Props = PropsWithChildren<{
  name: "project" | "more" | "mode" | "view" | "tools" | "representation";
  title: string;
}>;

type PanelProps = PropsWithChildren<{
  title: string;
  open: boolean;
  onClose(): void;
  project?: boolean;
}>;

/** @returns A settings page that replaces the View launcher and returns to it on close. */
export function MobileViewerSubmenu({
  open,
  onClose,
  children,
  ...props
}: PanelProps): JSX.Element {
  const mobileUi = useMobileViewerUi();
  const setSurface = mobileUi?.setSurface;
  useEffect(() => {
    if (open) setSurface?.(null);
  }, [open, setSurface]);
  return (
    <MobileViewerPanel
      {...props}
      open={open}
      onClose={() => {
        onClose();
        setSurface?.("view");
      }}
    >
      <MenuList component="div" autoFocusItem={false} sx={{ p: 0 }}>
        {children}
      </MenuList>
    </MobileViewerPanel>
  );
}

/** @returns A phone-sized transient panel which preserves its action owners while closed. */
export function MobileViewerSheet({
  name,
  title,
  children,
}: Props): JSX.Element {
  const mobileUi = useMobileViewerUi();
  return (
    <MobileViewerPanel
      title={title}
      open={mobileUi?.surface === name}
      onClose={() => mobileUi?.setSurface(null)}
      project={name === "project"}
    >
      {children}
    </MobileViewerPanel>
  );
}

/** @returns A reusable phone panel for both launchers and their settings pages. */
export function MobileViewerPanel({
  title,
  open,
  onClose,
  project = false,
  children,
}: PanelProps): JSX.Element {
  return (
    <Drawer
      anchor={project ? "left" : "bottom"}
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      slotProps={{
        paper: {
          role: "dialog",
          "aria-label": title,
          sx: {
            width: project ? "min(360px, 100vw)" : "100%",
            maxHeight: project ? "100dvh" : "65dvh",
            "@media (max-height: 599.95px)": {
              maxHeight: project ? "100dvh" : "85dvh",
            },
            bgcolor: "#20252b",
            color: "#fff",
            borderRadius: project ? 0 : "8px 8px 0 0",
            pb: "env(safe-area-inset-bottom, 0px)",
            overflow: "hidden",
            "& button": { minWidth: 44, minHeight: 44 },
          },
        },
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          minHeight: 52,
          flexShrink: 0,
        }}
      >
        <Typography component="h2" sx={{ fontSize: 16, fontWeight: 600 }}>
          {title}
        </Typography>
        <FaroIconButton
          dark
          aria-label={`Close ${title}`}
          onClick={onClose}
          sx={{ color: "inherit" }}
        >
          <CloseIcon />
        </FaroIconButton>
      </Stack>
      <Box
        sx={{
          p: 2,
          pt: 0,
          overflow: "auto",
          minHeight: 0,
          ...(project && { flex: 1, display: "flex", flexDirection: "column" }),
          overscrollBehavior: "contain",
          "& > .MuiStack-root": { minWidth: 0 },
        }}
      >
        {children}
      </Box>
    </Drawer>
  );
}
